/**
 * adminContentMutations — the single-item admin content write mutations:
 * create, save-draft, and publish-with-reconcile. Part of the
 * `useAdminContent` decomposition.
 *
 * Mutations expose the standard React Query
 * `useMutation<TData, TError, TVariables>` triple so consumers can branch on
 * `mutation.error`, `mutation.isError`, `mutation.data` without re-rolling
 * status state.
 *
 * Publish reconcile-on-error
 * ==========================
 *
 * `usePublishWithReconcile` wraps the one publish procedure with a
 * mandatory reconcile path: on `onError` it fires
 * `admin.content.get` to determine whether the publish DID land
 * server-side (the Slice-8 latent bug class — the DB commits before
 * the tRPC output validator runs, so an INTERNAL_SERVER_ERROR from
 * output validation does NOT guarantee the publish was rolled back).
 *
 *   - Reconciled `state === 'published'` → surface a success-ish
 *     message and DO NOT offer retry (a retry would hit
 *     `nothing-to-publish`).
 *   - Reconciled `state !== 'published'` (or reconcile itself fails)
 *     → surface the original error and offer retry.
 *
 * The reconcile state is exposed via `reconcile` so the form
 * component can branch its UI on the post-error truth (success-after-
 * reconcile vs. genuine failure).
 */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { WebTrpcClient } from '../../../trpc/client.js';
import {
  ADMIN_CONTENT_LIST_QUERY_KEY,
  ADMIN_FIXED_ENTRIES_QUERY_KEY,
  type AdminContentDetail,
  adminContentGetQueryKey,
} from './adminContentQueries.js';
import type { PublishScopeOrderInput } from './adminOrderingScheduling.js';
import { useAdminTrpc } from './adminTrpcContext.js';
import { useCommittedToast } from './lifecycleToast.js';

// ---------------------------------------------------------------------
// Create mutation
// ---------------------------------------------------------------------

/**
 * Input shape inferred from the `admin.content.create` procedure.
 * Mirrors the per-shape discriminated payload union the server's
 * `createInputSchema` defines (shape / slug / locale / categoryId /
 * initialPayload).
 */
export type CreateContentInput = Parameters<
  WebTrpcClient['admin']['content']['create']['mutate']
>[0];

export type CreateContentResult = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['create']['mutate']>
>;

/**
 * Mutate `admin.content.create`. Used by T095's linked-card create
 * flow: when the editor picks a tab for an article that has no
 * linked home_card yet, the form synthesises a new home_card with the
 * parent's `categoryId` (from the now-widened detail DTO) and fires
 * this mutation. The caller is responsible for cache invalidation on
 * success — typically a `queryClient.invalidateQueries({ queryKey:
 * ['admin', 'content', 'list'] })` so the new row appears in the
 * cached list that `useArticleTeaserHomeCard` consults.
 */
export function useCreateContent(): UseMutationResult<
  CreateContentResult,
  Error,
  CreateContentInput
> {
  const client = useAdminTrpc();
  return useMutation<CreateContentResult, Error, CreateContentInput>({
    mutationFn: (input) => client.admin.content.create.mutate(input),
  });
}

// ---------------------------------------------------------------------
// Create-unit mutation
// ---------------------------------------------------------------------

/**
 * Input shape inferred from the `admin.content.createUnit` procedure — the
 * article and its teaser, given once.
 *
 * Note what is NOT on it: the teaser carries no `locale`, no `categoryId`
 * and no `parentId`. It inherits the first two from the root and the server
 * sets the third inside the transaction, so the client cannot state them and
 * cannot state them wrongly.
 */
export type CreateUnitInput = Parameters<
  WebTrpcClient['admin']['content']['createUnit']['mutate']
>[0];

export type CreateUnitResult = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['createUnit']['mutate']>
>;

/**
 * Mutate `admin.content.createUnit` — the one call that creates an article
 * (D-198, owner ruling 20).
 *
 * The create flow used to send two: the page, then the teaser with the page's
 * id. Whatever failed the second one — transport, a slug collision, a closed
 * laptop — left the first committed, and the author had an article no member
 * could reach. One call, one transaction: it commits both rows or neither, so
 * there is no partial state for a caller to detect or recover from.
 */
/**
 * Narrow a standalone-create input down to the teaser a unit carries.
 *
 * The shape-specific derivations (`deriveLinkedHomeCardCreateInput`,
 * `deriveLinkedSidebarLinkCreateInput`) still describe a whole create,
 * because they also serve the flows that create a teaser on its own. Inside a
 * unit the server owns `locale`, `categoryId` and `parentId` — they are the
 * root's, or the root itself — so they are DROPPED here rather than sent and
 * ignored. A field the client cannot state is a field it cannot state wrongly.
 */
export function toUnitTeaserInput(input: CreateContentInput): CreateUnitInput['teaser'] {
  if (input.shape !== 'home_card' && input.shape !== 'sidebar_link') {
    throw new Error(`toUnitTeaserInput: ${input.shape} is not a teaser shape`);
  }
  return {
    shape: input.shape,
    slug: input.slug,
    initialPayload: input.initialPayload,
  } as CreateUnitInput['teaser'];
}

export function useCreateUnit(): UseMutationResult<CreateUnitResult, Error, CreateUnitInput> {
  const client = useAdminTrpc();
  return useMutation<CreateUnitResult, Error, CreateUnitInput>({
    mutationFn: (input) => client.admin.content.createUnit.mutate(input),
  });
}

// ---------------------------------------------------------------------
// Save-draft mutation
// ---------------------------------------------------------------------

/**
 * Input shape inferred from the `admin.content.saveDraft` procedure.
 * Lifting the type from the client surface keeps the editor's payload
 * shape locked to the wire contract — the union of payloads,
 * `expectedEtag`'s `UUID | '' | null` admission, the `id` UUID — all
 * flow through without restating.
 */
export type SaveDraftInput = Parameters<
  WebTrpcClient['admin']['content']['saveDraft']['mutate']
>[0];

export type SaveDraftResult = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['saveDraft']['mutate']>
>;

/**
 * Mutate `admin.content.saveDraft`. The caller composes the
 * `expectedEtag` per the wire contract (`null` or `''` for the
 * first-edit sentinel; otherwise the server-supplied UUID echoed back
 * from a prior save or load). A successful response replaces the exact
 * detail-cache entry with the server-normalized item, including its next
 * draft ETag, before callers advance, dismiss, or make another save.
 */
/**
 * NOTE: this hook does NOT announce. A save draft is the one action whose
 * extent the mutation cannot see: one "Tallenna" on an article with a linked
 * card runs TWO of these, and a run where the article committed and the card
 * failed is a partial save — the author's action did not succeed. Announcing
 * per mutation would toast twice for one click, and would claim success on the
 * partial path.
 *
 * The toast therefore belongs to the save HANDLER, which knows whether the
 * whole action landed. Every other lifecycle action is one mutation, so those
 * announce from their hook.
 */
export function useSaveDraft(): UseMutationResult<SaveDraftResult, Error, SaveDraftInput> {
  const client = useAdminTrpc();
  const queryClient = useQueryClient();
  return useMutation<SaveDraftResult, Error, SaveDraftInput>({
    mutationFn: (input) => client.admin.content.saveDraft.mutate(input),
    onSuccess: async (result, input) => {
      const detailQueryKey = adminContentGetQueryKey(input.id);

      // A slow pre-save get must not land after this authoritative mutation
      // response and restore the old payload or ETag.
      await queryClient.cancelQueries({ queryKey: detailQueryKey });
      queryClient.setQueryData(detailQueryKey, result.item);

      // Lists carry draft titles and state. Their exact variants include
      // locale/retired filters, so sweep the shared list-key family.
      void queryClient.invalidateQueries({ queryKey: ADMIN_CONTENT_LIST_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ADMIN_FIXED_ENTRIES_QUERY_KEY });
    },
  });
}

// ---------------------------------------------------------------------
// Publish-with-reconcile mutation
// ---------------------------------------------------------------------

export type PublishInput = Parameters<
  WebTrpcClient['admin']['content']['publishUnit']['mutate']
>[0];

export type PublishResult = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['publishUnit']['mutate']>
>;

/**
 * The scope whose STAGED order this item's publish should also commit
 * (024-cms / D-128 — ordering-as-draft). When present, a successful item
 * publish folds in `publishScopeOrder` for this scope so the item's draft
 * ordering goes live through the SAME publish action (the D-128 publish-time
 * fold). This coexists with the standalone "Julkaise järjestys" control
 * (`OrderPlanSection`); both commit via the same idempotent `publishScopeOrder`.
 * The scope is the row's ORDERING GROUP: one selector per group, keyed by the
 * shape. It used to be derived per entity — an article by its category, a card
 * by its Home tab — which is the model the catalog retired when the group
 * became the thing an author arranges. Omitted for shapes that carry no
 * ordering group (an article, a banner); the fold then does nothing.
 * `publishScopeOrder` is a no-op when nothing was staged, so this is safe to
 * pass on every publish.
 */
export type PublishScopeOrderDescriptor = PublishScopeOrderInput['scope'] & {
  readonly locale: PublishScopeOrderInput['locale'];
};

/**
 * The editor's publish variables: the wire `publish` input plus the optional
 * scope-order fold descriptor. The descriptor is stripped before the wire call
 * and drives a post-publish `publishScopeOrder` (D-128).
 */
export type PublishWithScopeOrderInput = PublishInput & {
  readonly scopeOrder?: PublishScopeOrderDescriptor;
};

/**
 * State of the post-publish reconcile probe.
 *
 *   - `'idle'`     — publish has not surfaced an error (or has not
 *                    been called yet). The form treats the publish
 *                    result normally.
 *   - `'pending'`  — publish errored; the reconcile probe is in
 *                    flight.
 *   - `'publishedDespiteError'` — the publish DID land at the database
 *                    layer despite the surfaced error. The form
 *                    SHOULD surface a success-ish message and SHOULD
 *                    NOT offer a retry button (a retry would hit
 *                    `nothing-to-publish`).
 *   - `'genuineError'` — publish failed AND the reconcile probe
 *                    confirms the item is not in `state: 'published'`
 *                    (or the reconcile itself failed; we surface the
 *                    original publish error in that case). The form
 *                    SHOULD offer a retry button.
 */
export type ReconcileState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'publishedDespiteError'; readonly detail: AdminContentDetail }
  | { readonly kind: 'genuineError'; readonly originalError: Error };

export interface PublishWithReconcileResult {
  /** Standard React Query mutation triple for the one publish procedure.
   * The variables type carries the optional D-128 `scopeOrder` fold
   * descriptor alongside the wire input (the unit root's id). */
  readonly mutation: UseMutationResult<PublishResult, Error, PublishWithScopeOrderInput>;
  /** Post-error reconcile state. `'idle'` until the first error fires. */
  readonly reconcile: ReconcileState;
}

/**
 * Publish the publication unit rooted at `unitRootId`, with the
 * reconcile-on-error path mandated by the slice brief.
 *
 * ONE PROCEDURE, ADDRESSED TO THE ROOT. The caller passes `parentId ?? id`
 * — an address, not a topology verdict — and the server resolves what the
 * unit contains. A unit of one takes exactly this path; there is no second
 * door to pick and nothing client-side left to derive.
 *
 * The lifecycle:
 *
 *   1. Caller invokes `result.mutation.mutate({ unitRootId })` (optionally with
 *      a `scopeOrder` descriptor — see below).
 *   2a. Success → `mutation.data` carries the publish DTO; `reconcile`
 *       remains `'idle'`.
 *   2b. Error  → reconcile is flipped to `'pending'` and we call
 *       `admin.content.get` for the same `id`.
 *      • If the reconciled detail's `state === 'published'`, reconcile
 *        becomes `'publishedDespiteError'` (carries the detail for any
 *        downstream "show the freshly-published state" hooks).
 *      • Otherwise (or if the reconcile probe itself throws),
 *        reconcile becomes `'genuineError'` (carries the original
 *        publish error so the UI can surface it verbatim).
 *
 * Ordering-as-draft fold (024-cms / D-128)
 * ========================================
 * When `mutate` carries a `scopeOrder` descriptor, a SUCCESSFUL item publish
 * also commits that scope's staged order via `publishScopeOrder`: the order
 * goes live through the SAME publish action (the publish-time fold). This fold
 * COEXISTS with the standalone "Julkaise järjestys" control (`OrderPlanSection`),
 * both invoking the same idempotent `publishScopeOrder`. The fold runs in `onSuccess` (after the item publish has landed) so
 * a scope-order failure NEVER misreports the item publish as failed: the item
 * is already live, and the order op is a no-op when nothing was staged for the
 * scope. The `admin.content.list` cache is invalidated after the order commit
 * (success OR failure) so the catalog re-reads the true server order — on a
 * failed fold that re-read clears the editor's optimistic (un-applied) overlay
 * rather than leaving it stranded as if it had gone live. A fold failure is
 * LOGGED (and a stale-baseline `STALE_ARRANGEMENT` conflict is distinguished
 * from a generic transport failure) rather than swallowed silently. The
 * descriptor is stripped before the wire `publish` call.
 *
 * The retry semantics live in the consumer: when reconcile is
 * `'publishedDespiteError'` the form omits the retry button; when it
 * is `'genuineError'` the form shows retry that fires
 * `mutation.mutate({ unitRootId })` again.
 */
export function usePublishWithReconcile(): PublishWithReconcileResult {
  const client = useAdminTrpc();
  const queryClient = useQueryClient();
  const announce = useCommittedToast();
  const [reconcile, setReconcile] = useState<ReconcileState>({ kind: 'idle' });

  const mutation = useMutation<PublishResult, Error, PublishWithScopeOrderInput>({
    // The `scopeOrder` fold descriptor is consumer-side only — strip it so the
    // wire publish call sees the contract input exactly.
    mutationFn: ({ scopeOrder: _scopeOrder, ...publishInput }) =>
      client.admin.content.publishUnit.mutate(publishInput),
    onMutate: () => {
      // Clear any prior reconcile state so a retry that succeeds does
      // not leave a stale `publishedDespiteError` banner on screen.
      setReconcile({ kind: 'idle' });
    },
    onSuccess: async (_result, { scopeOrder }) => {
      setReconcile({ kind: 'idle' });
      announce('cms.publish.success');
      // D-128 fold: commit the scope's staged order through the same publish.
      // A no-op when nothing was staged; a failure here does not unpublish the
      // item (already live), so it is awaited-but-not-rethrown.
      if (scopeOrder !== undefined) {
        try {
          await client.admin.content.publishScopeOrder.mutate({
            scope: { kind: scopeOrder.kind, key: scopeOrder.key },
            locale: scopeOrder.locale,
          });
          void queryClient.invalidateQueries({ queryKey: ADMIN_CONTENT_LIST_QUERY_KEY });
          void queryClient.invalidateQueries({ queryKey: ADMIN_FIXED_ENTRIES_QUERY_KEY });
        } catch (orderError) {
          // The item publish ALREADY succeeded — an order-apply failure must
          // not flip that success (so we do NOT rethrow). But the previous
          // bare-`catch {}` swallowed the failure silently, hiding a real
          // problem and leaving the catalog showing the editor's optimistic
          // (un-applied) order as if it had gone live. We now:
          //
          //   1. Distinguish a STALE_ARRANGEMENT conflict — the scope drifted
          //      under a concurrent edit, so the staged order is against a
          //      stale baseline and the editor must re-read + re-stage — from a
          //      generic transport/server failure. The publishScopeOrder
          //      rejection for a stale arrangement is a tRPC `CONFLICT` whose
          //      `message` contains the literal `STALE_ARRANGEMENT` marker (the
          //      admin-content-router maps `StaleArrangementError` to that).
          //   2. LOG the failure with that classification + the scope so it is
          //      not lost (apps/web has no logging abstraction — `console.error`
          //      is the surfacing convention here).
          //   3. INVALIDATE the same `admin.content.list` query the success
          //      path sweeps, so the catalog re-reads the TRUE (un-applied)
          //      order rather than stranding the optimistic overlay.
          const isStaleArrangement =
            orderError instanceof Error && orderError.message.includes('STALE_ARRANGEMENT');
          // biome-ignore lint/suspicious/noConsole: deliberate observability for a swallowed fold failure — the item publish outcome must not change, but the failure must not be invisible (the MED-1 fix)
          console.error(
            isStaleArrangement
              ? 'publishScopeOrder fold: staged order is stale (STALE_ARRANGEMENT) — the scope drifted, re-read; item publish already succeeded'
              : 'publishScopeOrder fold failed; item publish already succeeded',
            { scope: { kind: scopeOrder.kind, key: scopeOrder.key }, locale: scopeOrder.locale },
            orderError,
          );
          void queryClient.invalidateQueries({ queryKey: ADMIN_CONTENT_LIST_QUERY_KEY });
          void queryClient.invalidateQueries({ queryKey: ADMIN_FIXED_ENTRIES_QUERY_KEY });
        }
      }
    },
    onError: async (originalError, input) => {
      setReconcile({ kind: 'pending' });
      try {
        const detail = await client.admin.content.get.query({ id: input.unitRootId });
        if (detail.state === 'published') {
          setReconcile({ kind: 'publishedDespiteError', detail });
          // The publish LANDED — the probe just proved it. Same committed
          // fact, so the same sentence: the author is told what happened to
          // their content, not that our response stumbled on the way back.
          announce('cms.publish.success');
        } else {
          setReconcile({ kind: 'genuineError', originalError });
        }
      } catch {
        // Reconcile probe itself failed — we cannot distinguish
        // "publish landed" from "publish rolled back", so surface the
        // original error and let the user retry. A retry that hits
        // `nothing-to-publish` would mean the publish HAD landed; that
        // path is acceptable given we cannot prove the state-flip
        // without a successful get-query.
        setReconcile({ kind: 'genuineError', originalError });
      }
    },
    onSettled: () => {
      // Success AND error: the SERIALIZABLE transaction commits before the
      // router's output validation, so a publish that surfaces as an error
      // may still have landed (the `publishedDespiteError` arm above proves
      // exactly that). Both the list family and a PREFIX sweep over every
      // cached detail go, because a publish moves every component of the
      // unit and their ids are server-side knowledge — this layer cannot
      // enumerate them and must not pretend it can.
      void queryClient.invalidateQueries({ queryKey: ADMIN_CONTENT_LIST_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ADMIN_FIXED_ENTRIES_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'content', 'get'] });
    },
  });

  return { mutation, reconcile };
}
